/**
 * What a MODIFY build will change about the service's own metadata (task 808).
 *
 * WHY THIS EXISTS. A User asked the Assistant to update a service's Description.
 * The conversation confirmed, the build reported success with a passing smoke
 * test, and the Description was untouched — the modify spec had no field for it,
 * so the requested text went somewhere that feeds nothing. The confirmation card
 * faithfully previewed a plan that would do nothing, because it previewed the
 * capability's shape and never its metadata.
 *
 * So a change to the Name, the Description or the path is now shown as a change:
 * the current value beside the proposed one, before "Yes, build it". A plan that
 * would change nothing shows nothing to change, which is exactly the signal that
 * was missing.
 *
 * Only DIFFERENCES render. A modify spec re-emits the whole desired end state,
 * so most of what it carries is unchanged by definition; echoing all of it back
 * as "changes" would bury the one line that matters.
 */
import type { ComposerSpec } from '@/types/composer';

export interface SpecMetadataCurrent {
  name?: string | null;
  description?: string | null;
  path?: string | null;
}

interface SpecMetadataChangesProps {
  spec: ComposerSpec;
  /** The service as it stands today. Absent while it loads, or off a resource page. */
  current?: SpecMetadataCurrent;
}

/** Trimmed, or null — so "" and "   " are both "not set". */
const value = (input: unknown): string | null => {
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  return trimmed === '' ? null : trimmed;
};

function ChangeRow({
  label,
  from,
  to,
  testId,
}: {
  label: string;
  from: string | null;
  to: string;
  testId: string;
}) {
  return (
    <div data-testid={testId} className="space-y-1">
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p className="text-xs text-gray-500 line-through dark:text-gray-400">
        {from ?? <span className="italic no-underline">Not set</span>}
      </p>
      <p className="text-xs text-gray-900 dark:text-gray-100">{to}</p>
    </div>
  );
}

export function SpecMetadataChanges({ spec, current }: SpecMetadataChangesProps) {
  const endpoint = spec.endpoint ?? {};

  const nextDescription = value(endpoint.description);
  const nextName = value(endpoint.name);
  const nextPath = value(endpoint.path);

  const currentDescription = value(current?.description);
  const currentName = value(current?.name);
  const currentPath = value(current?.path);

  /**
   * A field counts as a change only when it is present AND different. The path
   * is the one that most needs this: a modify spec copies it forward verbatim,
   * so showing it unconditionally would announce a path change on every single
   * change conversation — which is how a guessed path came to be displayed as
   * "POST /api/mw/innovate-assistant" for a service whose path is `assistant`.
   */
  const rows: { label: string; from: string | null; to: string; testId: string }[] = [];
  if (nextDescription && nextDescription !== currentDescription) {
    rows.push({
      label: 'Description',
      from: currentDescription,
      to: nextDescription,
      testId: 'spec-metadata-description',
    });
  }
  if (nextName && nextName !== currentName) {
    rows.push({ label: 'Name', from: currentName, to: nextName, testId: 'spec-metadata-name' });
  }
  if (nextPath && currentPath && nextPath !== currentPath) {
    rows.push({ label: 'Path', from: currentPath, to: nextPath, testId: 'spec-metadata-path' });
  }

  if (rows.length === 0) return null;

  return (
    <div
      data-testid="spec-metadata-changes"
      className="mt-3 space-y-3 rounded-md border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-800 dark:bg-amber-900/10"
    >
      <p className="text-xs font-medium text-amber-800 dark:text-amber-200">
        What changes on this service&apos;s page
      </p>
      {rows.map((row) => (
        <ChangeRow key={row.testId} {...row} />
      ))}
    </div>
  );
}

export default SpecMetadataChanges;
